<template>
  <div class="contact-advanced-filters flex flex-col">
    <div
      v-if="!skipHeader"
      class="flex flex-col items-start border-b border-gray-100 pt-6 px-6 pb-4"
      style="box-shadow: 0px 1px 4px rgba(202, 202, 202, 0.16)"
    >
      <div
        class="text-gray-800 text-lg leading-5 dark:text-slate-100 font-semibold"
      >
        <span class="flex items-center gap-2">
          <v2-icon icon="filter_alt" :size="24" class="text-gray-500" />
          {{ filterModalHeaderTitle }}
        </span>
      </div>
      <div class="text-sm leading-5 text-gray-600 dark:text-slate-200 mt-2">
        {{ filterModalSubTitle }}
      </div>
    </div>

    <div class="px-6 py-4 flex-1">
      <div v-if="isSegmentsView">
        <label class="input-label" :class="{ error: !activeSegmentNewName }">
          {{ $t('CONTACTS_FILTER.SEGMENT_LABEL') }}
          <input
            v-model="activeSegmentNewName"
            type="text"
            class="folder-input border-slate-75 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
          />
          <span v-if="!activeSegmentNewName" class="message">
            {{ $t('CONTACTS_FILTER.EMPTY_VALUE_ERROR') }}
          </span>
        </label>
        <label class="input-label">
          {{ $t('CONTACTS_FILTER.SEGMENT_QUERY_LABEL') }}
        </label>
      </div>
      <div class="flex flex-col">
        <filter-input-box
          v-for="(filter, i) in appliedFilters"
          :key="i"
          v-model="appliedFilters[i]"
          :filter-groups="filterGroups"
          :grouped-filters="true"
          :input-type="
            getInputType(
              appliedFilters[i].attribute_key,
              appliedFilters[i].filter_operator
            )
          "
          :operators="getOperators(appliedFilters[i].attribute_key)"
          :dropdown-values="getDropdownValues(appliedFilters[i].attribute_key)"
          :show-query-operator="i !== appliedFilters.length - 1"
          :show-user-input="showUserInput(appliedFilters[i].filter_operator)"
          :show-filter-operator="true"
          :show-filter-type="true"
          :show-input-label="true"
          :v="$v.appliedFilters.$each[i]"
          :disable-remove-filter="appliedFilters.length === 1"
          @resetFilter="resetFilter(i, appliedFilters[i])"
          @removeFilter="removeFilter(i)"
        />

        <div class="mt-4">
          <button-v2
            variant="outline"
            size="md"
            icon-left="add"
            class="add-filter-btn"
            @click="appendNewFilter"
          >
            {{ $t('CONTACTS_FILTER.ADD_NEW_FILTER') }}
          </button-v2>
          <button-v2
            v-if="hasAppliedFilters && !isSegmentsView"
            variant="ghost"
            size="md"
            icon-left="remove"
            class="add-filter-btn ml-3"
            @click="clearFilters"
          >
            {{ $t('CONTACTS_FILTER.CLEAR_ALL_FILTERS') }}
          </button-v2>
        </div>
      </div>
    </div>

    <div
      v-if="!skipButtons"
      class="w-full border-t border-gray-100 px-6 pt-4 pb-6"
      style="box-shadow: 0px -1px 4px rgba(202, 202, 202, 0.16)"
    >
      <div class="flex flex-row justify-end gap-3 px-0 w-full">
        <button-v2
          variant="naked"
          size="lg"
          class="cancel-filter-btn"
          @click.prevent="onClose"
        >
          {{ $t('CONTACTS_FILTER.CANCEL_BUTTON_LABEL') }}
        </button-v2>
        <button-v2
          v-if="isSegmentsView"
          variant="filled"
          size="lg"
          :disabled="!activeSegmentNewName"
          class="apply-filter-btn"
          @click="updateSegment"
        >
          {{ $t('CONTACTS_FILTER.UPDATE_BUTTON_LABEL') }}
        </button-v2>
        <button-v2
          v-else
          variant="filled"
          size="lg"
          class="apply-filter-btn"
          :disabled="!hasValidFilters"
          @click="submitFilterQuery"
        >
          {{ $t('CONTACTS_FILTER.SUBMIT_BUTTON_LABEL') }}
        </button-v2>
      </div>
    </div>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import { required, requiredIf } from 'vuelidate/lib/validators';
import FilterInputBox from '../../../../components/widgets/FilterInput/IndexV2.vue';
import V2Icon from 'v2/components/Icon.vue';
import ButtonV2 from 'v2/components/Button/ButtonV2.vue';
import countries from 'shared/constants/countries.js';
import { mapGetters } from 'vuex';
import { filterAttributeGroups } from '../contactFilterItems';
import filterMixin from 'shared/mixins/filterMixin';
import * as OPERATORS from 'dashboard/components/widgets/FilterInput/FilterOperatorTypes.js';
import { CONTACTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
export default {
  components: {
    FilterInputBox,
    V2Icon,
    ButtonV2,
  },
  mixins: [alertMixin, filterMixin],
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
    initialFilterTypes: {
      type: Array,
      default: () => [],
    },
    initialAppliedFilters: {
      type: Array,
      default: () => [],
    },
    isSegmentsView: {
      type: Boolean,
      default: false,
    },
    activeSegmentName: {
      type: String,
      default: '',
    },
    skipHeader: {
      type: Boolean,
      default: false,
    },
    skipButtons: {
      type: Boolean,
      default: false,
    },
  },
  validations: {
    appliedFilters: {
      required,
      $each: {
        values: {
          ensureBetween0to999(value, prop) {
            if (prop.filter_operator === 'days_before') {
              return parseInt(value, 10) > 0 && parseInt(value, 10) < 999;
            }
            return true;
          },
          required: requiredIf(prop => {
            return !(
              prop.filter_operator === 'is_present' ||
              prop.filter_operator === 'is_not_present'
            );
          }),
        },
      },
    },
  },
  data() {
    return {
      show: true,
      appliedFilters: this.initialAppliedFilters,
      activeSegmentNewName: this.activeSegmentName,
      filterTypes: this.initialFilterTypes,
      filterGroups: [],
      allCustomAttributes: [],
      filterAttributeGroups,
      attributeModel: 'contact_attribute',
      filtersFori18n: 'CONTACTS_FILTER',
    };
  },
  computed: {
    ...mapGetters({
      getAppliedContactFilters: 'contacts/getAppliedContactFilters',
    }),
    hasAppliedFilters() {
      return this.getAppliedContactFilters.length;
    },
    filterModalHeaderTitle() {
      return !this.isSegmentsView
        ? this.$t('CONTACTS_FILTER.TITLE')
        : this.$t('CONTACTS_FILTER.EDIT_CUSTOM_SEGMENT');
    },
    filterModalSubTitle() {
      return !this.isSegmentsView
        ? this.$t('CONTACTS_FILTER.SUBTITLE')
        : this.$t('CONTACTS_FILTER.CUSTOM_VIEWS_SUBTITLE');
    },
    hasValidFilters() {
      return this.appliedFilters.some(filter => {
        if (!filter.attribute_key) return false;
        const op = filter.filter_operator;
        if (op === 'is_present' || op === 'is_not_present') return true;
        if (Array.isArray(filter.values)) return filter.values.length > 0;
        return (
          filter.values !== '' &&
          filter.values !== null &&
          filter.values !== undefined
        );
      });
    },
  },
  watch: {
    appliedFilters: {
      handler(value) {
        this.$emit('onFiltersChange', value);
      },
    },
  },
  mounted() {
    this.setFilterAttributes();
    if (this.getAppliedContactFilters.length) {
      this.appliedFilters = [...this.getAppliedContactFilters];
    } else if (!this.isSegmentsView && !this.appliedFilters.length) {
      this.appliedFilters.push({
        attribute_key: 'name',
        filter_operator: 'equal_to',
        values: '',
        query_operator: 'and',
        attribute_model: 'standard',
      });
    }
  },
  methods: {
    getOperatorTypes(key) {
      switch (key) {
        case 'list':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'text':
          return OPERATORS.OPERATOR_TYPES_3;
        case 'number':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'link':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'date':
          return OPERATORS.OPERATOR_TYPES_4;
        case 'checkbox':
          return OPERATORS.OPERATOR_TYPES_1;
        default:
          return OPERATORS.OPERATOR_TYPES_1;
      }
    },
    customAttributeInputType(key) {
      switch (key) {
        case 'date':
          return 'date';
        case 'text':
          return 'plain_text';
        case 'list':
          return 'search_select';
        case 'checkbox':
          return 'search_select';
        default:
          return 'plain_text';
      }
    },
    getAttributeModel(key) {
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      return type.attributeModel;
    },
    getInputType(key, operator) {
      if (key === 'created_at' || key === 'last_activity_at')
        if (operator === 'days_before') return 'plain_text';
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      return type?.inputType;
    },
    getOperators(key) {
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      return type?.filterOperators;
    },
    getDropdownValues(type) {
      const allCustomAttributes = this.$store.getters[
        'attributes/getAttributesByModel'
      ](this.attributeModel);
      const isCustomAttributeCheckbox = allCustomAttributes.find(attr => {
        return (
          attr.attribute_key === type &&
          attr.attribute_display_type === 'checkbox'
        );
      });
      const TRUE_FALSE_OPTIONS = [
        {
          id: true,
          name: this.$t('FILTER.ATTRIBUTE_LABELS.TRUE'),
        },
        {
          id: false,
          name: this.$t('FILTER.ATTRIBUTE_LABELS.FALSE'),
        },
      ];
      if (isCustomAttributeCheckbox) {
        return TRUE_FALSE_OPTIONS;
      }

      const isCustomAttributeList = allCustomAttributes.find(attr => {
        return (
          attr.attribute_key === type && attr.attribute_display_type === 'list'
        );
      });

      if (isCustomAttributeList) {
        return allCustomAttributes
          .find(attr => attr.attribute_key === type)
          .attribute_values.map(item => {
            return {
              id: item,
              name: item,
            };
          });
      }
      switch (type) {
        case 'labels':
          return this.$store.getters['labels/getLabels'].map(i => {
            return {
              id: i.title,
              name: i.title,
            };
          });
        case 'country_code':
          return countries;
        case 'blocked':
          return TRUE_FALSE_OPTIONS;
        default:
          return undefined;
      }
    },
    appendNewFilter() {
      if (this.isSegmentsView) {
        this.setQueryOperatorOnLastQuery();
      } else {
        this.appliedFilters.push({
          attribute_key: 'name',
          filter_operator: 'equal_to',
          values: '',
          query_operator: 'and',
        });
      }
    },
    setQueryOperatorOnLastQuery() {
      const lastItemIndex = this.appliedFilters.length - 1;
      this.appliedFilters[lastItemIndex] = {
        ...this.appliedFilters[lastItemIndex],
        query_operator: 'and',
      };
      this.$nextTick(() => {
        this.appliedFilters.push({
          attribute_key: 'name',
          filter_operator: 'equal_to',
          values: '',
          query_operator: 'and',
        });
      });
    },
    removeFilter(index) {
      if (this.appliedFilters.length <= 1) {
        this.showAlert(this.$t('CONTACTS_FILTER.FILTER_DELETE_ERROR'));
      } else {
        this.appliedFilters.splice(index, 1);
      }
    },
    isFiltersValid() {
      this.$v.$touch();
      return !this.$v.$invalid;
    },
    submitFilterQuery() {
      if (!this.isFiltersValid) return;
      this.$emit('applyFilter', this.appliedFilters);
      this.$store.dispatch(
        'contacts/setContactFilters',
        JSON.parse(JSON.stringify(this.appliedFilters))
      );
      this.$track(CONTACTS_EVENTS.APPLY_FILTER, {
        applied_filters: this.appliedFilters.map(filter => ({
          key: filter.attribute_key,
          operator: filter.filter_operator,
          query_operator: filter.query_operator,
        })),
      });
    },
    updateSegment() {
      this.$emit(
        'updateSegment',
        this.appliedFilters,
        this.activeSegmentNewName
      );
    },
    resetFilter(index, currentFilter) {
      this.appliedFilters[index].filter_operator = this.filterTypes.find(
        filter => filter.attributeKey === currentFilter.attribute_key
      ).filterOperators[0].value;
      this.appliedFilters[index].values = '';
    },
    showUserInput(operatorType) {
      if (operatorType === 'is_present' || operatorType === 'is_not_present')
        return false;
      return true;
    },
    clearFilters() {
      this.$emit('clearFilters');
      this.onClose();
    },
  },
};
</script>
<style lang="scss">
.contact-advanced-filter-modal {
  .modal-container {
    @apply rounded-[12px];
    overflow: visible;
    width: 700px;
    max-width: calc(100vw - 2rem);

    .woot-modal-header {
      @apply pt-6 px-6;
    }
  }

  .contact-advanced-filters {
    max-height: 90vh;
  }

  .multiselect__content-wrapper {
    z-index: 100;
  }
}
</style>
<style lang="scss" scoped>
.folder-input {
  @apply w-[50%];
}

.add-filter-btn.button-v2--md {
  font-size: 16px;
  line-height: 24px;
  padding: 7px 15px;
}
</style>
